import React from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';

/**
 * Overlay para conteúdo bloqueado por limites de visualização do Viewer
 * Mostra conteúdo borrado com CTA para assinatura
 */
const ViewerContentOverlay = ({ 
  creatorId, 
  creatorName = 'este criador',
  limitType = 'profile', // 'profile' ou 'global'
  photosViewed = 0,
  totalContent = 0,
  showStats = true
}) => {
  const navigate = useNavigate();
  const { user } = useAuth();

  const isProfileLimit = limitType === 'profile';

  const messages = {
    profile: {
      title: '🔥 Quer ver tudo desse perfil?',
      description: `Você já visualizou ${photosViewed} conteúdos gratuitos${totalContent > 0 ? ` de ${totalContent} disponíveis` : ''}. Desbloqueie acesso completo para ver fotos e vídeos exclusivos.`,
      cta: '👉 Desbloquear conteúdo'
    },
    global: {
      title: '😈 Seu acesso gratuito terminou por hoje',
      description: 'A comunidade Lyberhot é exclusiva. Para continuar explorando conteúdos explícitos sem limites, assine agora.',
      cta: '🔓 Continuar explorando'
    }
  };

  const content = messages[limitType] || messages.profile;

  const handleSubscribe = () => {
    if (!user) {
      navigate('/?mode=login');
      return;
    }
    // Redirecionar para página do criador com modal de assinatura
    if (creatorId) {
      navigate(`/creator/${creatorId}?subscribe=true`);
    } else {
      navigate('/feed');
    }
  };

  return (
    <div 
      className="absolute inset-0 flex flex-col items-center justify-center z-10"
      style={{
        background: 'linear-gradient(180deg, rgba(0,0,0,0.4) 0%, rgba(0,0,0,0.8) 100%)',
        backdropFilter: 'blur(20px)',
        WebkitBackdropFilter: 'blur(20px)'
      }}
      data-testid="viewer-content-overlay"
    >
      <div className="text-center px-6 max-w-sm">
        {/* Icon */}
        <div className="text-5xl mb-4 animate-pulse">
          {isProfileLimit ? '🔥' : '😈'}
        </div>

        {/* Title */}
        <h3 className="text-xl font-bold text-white mb-3">
          {content.title}
        </h3>

        {/* Description */}
        <p className="text-gray-300 text-sm mb-5 leading-relaxed">
          {content.description}
        </p>

        {/* Stats Badge */}
        {showStats && isProfileLimit && photosViewed > 0 && (
          <div className="inline-flex items-center gap-2 bg-white/10 backdrop-blur-sm px-4 py-2 rounded-full mb-5">
            <span className="text-[#C79F5E]">👁️</span>
            <span className="text-sm text-white font-medium">
              {photosViewed} visualizações gratuitas usadas
            </span>
          </div>
        )}

        {/* CTA Button */}
        <button
          onClick={handleSubscribe}
          className="w-full bg-gradient-to-r from-[#4D1A4A] via-[#6B2D5C] to-[#C79F5E] text-white py-4 rounded-full font-bold text-lg hover:scale-105 active:scale-95 transition-all shadow-lg hover:shadow-[#C79F5E]/30"
          data-testid="viewer-limit-cta-btn"
        >
          {content.cta}
        </button>

        {/* Secondary Action */}
        {isProfileLimit && creatorName && (
          <p className="text-gray-500 text-xs mt-4">
            Assine <span className="text-[#C79F5E]">{creatorName}</span> para acesso ilimitado
          </p>
        )}

        {/* Global limit info */}
        {!isProfileLimit && (
          <p className="text-gray-500 text-xs mt-4">
            Limites resetam à meia-noite
          </p>
        )}
      </div>
    </div>
  );
};

export default ViewerContentOverlay;
